/**
 * Whether a tool only reads or changes something: an icon where space is short, a labelled pill
 * where it is not, and a filter over the two.
 */

import { Eye, PencilLine, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Segmented } from '@/modules/simulator/ui/segmented';
import { toolAccess, type ToolAccess } from './tool-filters';

const ACCESS: Record<ToolAccess, { icon: LucideIcon; label: string; hint: string }> = {
	read: { icon: Eye, label: 'Read', hint: 'Only reads data' },
	write: { icon: PencilLine, label: 'Write', hint: 'Changes data' }
};

export const AccessIcon = ({ riskLevel }: { riskLevel: string }) => {
	const access = toolAccess(riskLevel);
	const { icon: Icon, label, hint } = ACCESS[access];
	return (
		<span
			role='img'
			aria-label={label}
			title={hint}
			className={cn('shrink-0', access === 'write' ? 'text-text-heading' : 'text-gray-800')}
		>
			<Icon className='size-3.5' aria-hidden />
		</span>
	);
};

export const AccessBadge = ({ riskLevel }: { riskLevel: string }) => {
	const access = toolAccess(riskLevel);
	const { icon: Icon, label, hint } = ACCESS[access];
	return (
		<span
			title={hint}
			className={cn(
				'inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 text-[10px] leading-4',
				access === 'write' ? 'border-text-quote bg-ink text-white' : 'border-gray-500 text-gray-800'
			)}
		>
			<Icon className='size-2.5' aria-hidden />
			{label}
		</span>
	);
};

export const AccessFilter = ({
	value,
	onChange
}: {
	value: ToolAccess | 'all';
	onChange: (value: ToolAccess | 'all') => void;
}) => (
	<Segmented
		options={[
			{ value: 'all', label: 'All' },
			{ value: 'read', label: ACCESS.read.label, icon: ACCESS.read.icon },
			{ value: 'write', label: ACCESS.write.label, icon: ACCESS.write.icon }
		]}
		value={value}
		onChange={onChange}
	/>
);
